import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { ActionIcon, Menu, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { AddCircleOutlineOutlined, ContentCopy, DeleteOutline, EditOutlined, MoreVert, RemoveCircleOutline } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { playwright_action_menu_button } from "@therapprove/playwright";
import { useState } from "react";
import { doesArrayHaveData } from "../utils/Array.utils";
import { titleCase } from "../utils/String.utils";
import { Confirmation } from "./Confirmation";
export const ActionsMenu = ({ entityId, style, onClickEdit, isActive, menuOptions, allowEdit, allowDeactivate, allowArchive, allowDelete, handleActiveStatus, handleArchive, handleDelete }) => {
    // Delete/Archive Confirmation Modal State
    const [opened, { open, close }] = useDisclosure(false);
    const [isLoading, setIsLoading] = useState(false);
    const allowDeleteOrArchive = allowDelete || allowArchive;
    const deleteOrArchiveAction = allowDelete
        ? "delete"
        : allowArchive
            ? "archive"
            : "";
    const nonDestructiveMenuOptions = menuOptions?.filter((option) => !option.isDestructiveAction && (option.isVisible ?? true));
    const destructiveMenuOptions = menuOptions?.filter((option) => option.isDestructiveAction && (option.isVisible ?? true));
    const takeActionWithLoading = async () => {
        setIsLoading(true);
        try {
            if (allowArchive) {
                await handleArchive?.();
            }
            if (allowDelete) {
                await handleDelete?.();
            }
        }
        finally {
            setIsLoading(false);
            close();
        }
    };
    return (_jsxs("div", { style: {
            float: "right",
            marginLeft: "auto",
            marginRight: "1rem",
            ...style
        }, "data-testid": playwright_action_menu_button, children: [allowDeleteOrArchive && (_jsx(Confirmation, { opened: opened, close: close, action: deleteOrArchiveAction, handleAction: takeActionWithLoading, isLoading: isLoading })), _jsxs(Menu, { closeOnClickOutside: true, position: "bottom-end", styles: { dropdown: { minWidth: "10rem" } }, children: [_jsx(Menu.Target, { children: _jsx(ActionIcon, { variant: "subtle", onClick: (event) => {
                                event.stopPropagation();
                            }, children: _jsx(SvgIcon, { component: MoreVert }) }) }), _jsxs(Menu.Dropdown, { children: [allowEdit && (_jsx(Menu.Item, { leftSection: _jsx(SvgIcon, { component: EditOutlined, sx: { width: "0.8rem", height: "0.8rem" } }), onClick: (event) => {
                                    event.stopPropagation();
                                    onClickEdit?.();
                                }, children: "Edit" })), handleActiveStatus && allowDeactivate && (_jsx(Menu.Item, { leftSection: _jsx(SvgIcon, { component: isActive
                                        ? RemoveCircleOutline
                                        : AddCircleOutlineOutlined, sx: { width: "0.8rem", height: "0.8rem" } }), onClick: (event) => {
                                    event.stopPropagation();
                                    handleActiveStatus();
                                }, children: isActive ? "Deactivate" : "Activate" })), doesArrayHaveData(nonDestructiveMenuOptions) && (_jsx(_Fragment, { children: nonDestructiveMenuOptions.map((option, index) => (_jsx(Menu.Item, { leftSection: _jsx(SvgIcon, { component: option.icon, sx: {
                                            width: "0.8rem",
                                            height: "0.8rem"
                                        } }), color: option.color, onClick: (event) => {
                                        event.stopPropagation();
                                        option.onClick(event);
                                    }, children: option.text }, index))) })), entityId && (
                            // TODO: This margin and padding is stupid, figure out how to get this to line up
                            // without having to do this
                            _jsxs("div", { style: {
                                    marginLeft: "-0.3rem",
                                    paddingRight: "0.3rem"
                                }, children: [_jsx(Menu.Divider, {}), _jsx(Menu.Item, { leftSection: _jsx(SvgIcon, { component: ContentCopy, sx: {
                                                width: "0.8rem",
                                                height: "0.8rem",
                                                color: "var(--mantine-color-gray-4)"
                                            } }), "data-id-menu-item": "true", onClick: (event) => {
                                            event.stopPropagation();
                                            try {
                                                if (navigator?.clipboard) {
                                                    navigator.clipboard.writeText(entityId);
                                                }
                                                else {
                                                    // Fallback for browsers that don't support clipboard API
                                                    const textarea = document.createElement("textarea");
                                                    textarea.value = entityId;
                                                    textarea.style.position = "fixed"; // Avoid scrolling to bottom
                                                    document.body.appendChild(textarea);
                                                    textarea.focus();
                                                    textarea.select();
                                                    document.execCommand("copy");
                                                    document.body.removeChild(textarea);
                                                }
                                            }
                                            catch (err) {
                                                console.error("Failed to copy text: ", err);
                                            }
                                        }, children: _jsx(Text, { variant: "body", style: {
                                                color: "var(--mantine-color-gray-4)"
                                            }, children: entityId.split("-")[0] }) })] })), allowDeleteOrArchive && (_jsxs(_Fragment, { children: [doesArrayHaveData(destructiveMenuOptions) && (_jsx(_Fragment, { children: destructiveMenuOptions.map((option, index) => (_jsxs("div", { children: [_jsx(Menu.Divider, {}), _jsx(Menu.Item, { leftSection: _jsx(SvgIcon, { component: option.icon, sx: {
                                                            width: "0.8rem",
                                                            height: "0.8rem"
                                                        } }), "data-delete-item": "true", color: option.color, onClick: (event) => {
                                                        event.stopPropagation();
                                                        option.onClick(event);
                                                    }, children: option.text }, index)] }, index))) })), _jsx(Menu.Divider, {}), _jsx(Menu.Item, { leftSection: _jsx(SvgIcon, { component: DeleteOutline, sx: {
                                                width: "0.8rem",
                                                height: "0.8rem"
                                            }, style: {
                                                color: "var(--mantine-color-red-5)"
                                            } }), "data-delete-item": "true", onClick: (event) => {
                                            event.stopPropagation();
                                            open();
                                        }, children: _jsx(Text, { variant: "body", style: {
                                                color: "var(--mantine-color-red-5)"
                                            }, children: titleCase(deleteOrArchiveAction) }) })] }))] })] })] }));
};
